import { X } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { useReturnFocus } from './focus';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

type DialogContentProps = Omit<ComponentProps<typeof DialogPrimitive.Content>, 'title'> & {
  /** Always required: it names the dialog for assistive tech. */
  title: ReactNode;
  description?: ReactNode;
  /** Keep the title for screen readers but do not show it. */
  hideTitle?: boolean;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  showClose?: boolean;
};

/** A centred modal. Focus is trapped, Esc closes, focus returns to the opener. */
export function DialogContent({ title, description, hideTitle = false, footer, size = 'md', showClose = true, className, children, ...props }: DialogContentProps) {
  const returnFocus = useReturnFocus();
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-scrim data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in" />
      <DialogPrimitive.Content
        className={cn(
          'fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-32px)] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-card border border-line bg-surface text-fg shadow-pop outline-none data-[state=open]:animate-pop-in',
          size === 'sm' && 'max-w-[400px]',
          size === 'md' && 'max-w-[520px]',
          size === 'lg' && 'max-w-[760px]',
          className,
        )}
        {...(description ? {} : { 'aria-describedby': undefined })}
        onOpenAutoFocus={returnFocus.onOpenAutoFocus}
        onCloseAutoFocus={returnFocus.onCloseAutoFocus}
        {...props}
      >
        <div className={cn('flex items-start gap-2 px-6 pt-5', hideTitle && 'sr-only')}>
          <div className="min-w-0 flex-1">
            <DialogPrimitive.Title className="m-0 text-base font-semibold">{title}</DialogPrimitive.Title>
            {description ? (
              <DialogPrimitive.Description className="mt-1 mb-0 text-sm text-muted">{description}</DialogPrimitive.Description>
            ) : null}
          </div>
          {showClose && !hideTitle ? <DialogCloseButton /> : null}
        </div>
        <div className="min-h-0 overflow-y-auto px-6 py-4">{children}</div>
        {footer ? <div className="flex flex-wrap justify-end gap-2 border-t border-line px-6 py-4">{footer}</div> : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function DialogCloseButton({ label = 'Close' }: { label?: string }) {
  return (
    <DialogPrimitive.Close
      aria-label={label}
      className="grid size-[30px] shrink-0 place-items-center rounded-ctl border border-line bg-transparent text-muted hover:bg-wash hover:text-fg"
    >
      <X className="size-4" aria-hidden="true" />
    </DialogPrimitive.Close>
  );
}
